import { useCallback, useEffect, useRef, useState } from 'react';

import { ReactComponent as SearchIcon } from '@crazyegginc/hatch/dist/images/icon-search-filled.svg';

import { AdPreviewOverlay } from './AdPreviewOverlay';

const CLOSE_DELAY_MS = 120;

export function AdPreviewTrigger({ siteId, adId, adName }) {
  const buttonRef = useRef(null);
  const closeTimeoutRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const updateAnchorRect = useCallback(() => {
    if (buttonRef.current) {
      setAnchorRect(buttonRef.current.getBoundingClientRect());
    }
  }, []);

  const closePreview = useCallback(() => {
    clearCloseTimeout();
    setIsPinned(false);
    setIsOpen(false);
  }, [clearCloseTimeout]);

  const scheduleClose = useCallback(() => {
    clearCloseTimeout();

    closeTimeoutRef.current = window.setTimeout(() => {
      if (!isPinned) {
        setIsOpen(false);
      }
    }, CLOSE_DELAY_MS);
  }, [clearCloseTimeout, isPinned]);

  const openPreview = useCallback(() => {
    clearCloseTimeout();
    updateAnchorRect();
    setIsOpen(true);
  }, [clearCloseTimeout, updateAnchorRect]);

  useEffect(() => () => clearCloseTimeout(), [clearCloseTimeout]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    updateAnchorRect();

    const handleViewportChange = () => updateAnchorRect();

    window.addEventListener('scroll', handleViewportChange, true);
    window.addEventListener('resize', handleViewportChange);

    return () => {
      window.removeEventListener('scroll', handleViewportChange, true);
      window.removeEventListener('resize', handleViewportChange);
    };
  }, [isOpen, updateAnchorRect]);

  useEffect(() => {
    if (!isOpen || !isPinned) {
      return;
    }

    const handlePointerDown = (event) => {
      if (buttonRef.current?.contains(event.target)) {
        return;
      }

      closePreview();
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closePreview();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closePreview, isOpen, isPinned]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label={`Preview ${adName}`}
        className={[
          'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
          'text-cadet-blue-500 transition-colors hover:bg-solitude-500 hover:text-[#00509D]',
          'focus:bg-solitude-500 focus:text-[#00509D] focus:outline-none',
        ].join(' ')}
        onMouseEnter={openPreview}
        onMouseLeave={scheduleClose}
        onFocus={openPreview}
        onBlur={scheduleClose}
        onClick={() => {
          if (isOpen && isPinned) {
            closePreview();
            return;
          }

          openPreview();
          setIsPinned(true);
        }}
      >
        <span aria-hidden="true">
          <SearchIcon className="h-4 w-4 fill-current" />
        </span>
      </button>

      {isOpen ? (
        <AdPreviewOverlay
          siteId={siteId}
          adId={adId}
          fallbackAdName={adName}
          anchorRect={anchorRect}
          onMouseEnter={clearCloseTimeout}
          onMouseLeave={scheduleClose}
        />
      ) : null}
    </>
  );
}
